import { useCallback, useEffect, useReducer, useRef } from "react";

const BASE_URL = process.env.REACT_APP_IC_BACKEND_URL || "";

const initialState = {
  stages: [],
  streamedText: "",
  memories: [],
  conversationId: null,
  error: null,
  isStreaming: false,
  // Server-side persona variant === "distress" (finalize.js emits `persona` on
  // reply_meta). Drives the SupportResources card. Kept as a boolean rather
  // than the raw variant so the UI can't accidentally branch on "friend".
  isDistress: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "start":
      return {
        ...initialState,
        isStreaming: true,
        conversationId: state.conversationId,
      };

    case "stage_start": {
      const idx = state.stages.findIndex((s) => s.name === action.name);
      let stages;
      if (idx >= 0) {
        stages = state.stages.slice();
        stages[idx] = {
          ...stages[idx],
          status: "running",
          summary: undefined,
          duration_ms: undefined,
        };
      } else {
        stages = [
          ...state.stages,
          { name: action.name, label: action.label, status: "running" },
        ];
      }
      const clearText = action.name === "generation" && idx >= 0;
      return {
        ...state,
        stages,
        streamedText: clearText ? "" : state.streamedText,
      };
    }

    case "stage_done": {
      const idx = state.stages.findIndex((s) => s.name === action.name);
      if (idx < 0) return state;
      const stages = state.stages.slice();
      stages[idx] = {
        ...stages[idx],
        status: action.status || "done",
        summary: action.summary,
        duration_ms: action.duration_ms,
        error: action.error,
      };
      return { ...state, stages };
    }

    case "token":
      return {
        ...state,
        streamedText: state.streamedText + (action.text || ""),
      };

    case "reply_meta":
      return {
        ...state,
        memories: Array.isArray(action.memories) ? action.memories : state.memories,
        conversationId: action.conversationId || state.conversationId,
        isDistress: action.persona === "distress",
      };

    case "error":
      return { ...state, error: action.message || "Something flickered" };

    case "done":
      return { ...state, isStreaming: false };

    case "reset":
      // Clear conversationId too — parent owns it via props and re-supplies on
      // the next send. Preserving it here caused new chats to inherit the
      // previously-resumed id via the App.js echo effect (chat-history bug).
      return initialState;

    default:
      return state;
  }
}

export function useChatPipelineStream({ userId, conversationId }) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    conversationId: conversationId || null,
  });
  const abortRef = useRef(null);

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // `openerId` is set only on the first turn of a chat started from Home's
  // "Open Dialogue". The server looks the opener text up itself and saves it
  // as the conversation's first message; it never takes that text from here.
  const send = useCallback(
    async (chatInput, history, openerId = null) => {
      abort();
      dispatch({ type: "start" });

      const ac = new AbortController();
      abortRef.current = ac;

      const token =
        typeof window !== "undefined"
          ? window.localStorage.getItem("token")
          : null;

      let response;
      try {
        response = await fetch(`${BASE_URL}/api/ai/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/x-ndjson",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            userId,
            chatInput,
            history: Array.isArray(history) ? history.slice(-6) : [],
            conversationId: conversationId || null,
            ...(openerId ? { openerId } : {}),
          }),
          signal: ac.signal,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          dispatch({ type: "error", message: "Couldn't reach Mindwire" });
        }
        dispatch({ type: "done" });
        abortRef.current = null;
        return;
      }

      if (response.status === 401) {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("session-expired"));
        }
        dispatch({ type: "error", message: "Session expired" });
        dispatch({ type: "done" });
        abortRef.current = null;
        return;
      }

      if (!response.ok || !response.body) {
        dispatch({ type: "error", message: "Chat unavailable" });
        dispatch({ type: "done" });
        abortRef.current = null;
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let nl;
          while ((nl = buffer.indexOf("\n")) >= 0) {
            const line = buffer.slice(0, nl).trim();
            buffer = buffer.slice(nl + 1);
            if (!line) continue;
            try {
              dispatch(JSON.parse(line));
            } catch {
              // Skip malformed lines; keep streaming.
            }
          }
        }
        const tail = buffer.trim();
        if (tail) {
          try {
            dispatch(JSON.parse(tail));
          } catch {
            // ignore
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          dispatch({ type: "error", message: "Stream interrupted" });
        }
      } finally {
        abortRef.current = null;
        dispatch({ type: "done" });
      }
    },
    [userId, conversationId, abort]
  );

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  return { state, send, abort, reset };
}
